import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, Building2, MessageSquare, Newspaper, LogOut, ArrowLeft, BadgeCheck, Coins } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { LogoMark } from "../../components/Logo";
import api from "../../lib/api";
import FirmsTab from "./FirmsTab";
import ReviewsTab from "./ReviewsTab";
import BlogTab from "./BlogTab";
import ProofsTab from "./ProofsTab";
import CoinsTab from "./CoinsTab";

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "firms", label: "Firms", icon: Building2 },
  { key: "reviews", label: "Reviews", icon: MessageSquare },
  { key: "proofs", label: "Proofs", icon: BadgeCheck },
  { key: "coins", label: "Coins", icon: Coins },
  { key: "blog", label: "Journal", icon: Newspaper },
];

function Overview({ onNavigate }) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);
  if (!stats) return <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted">LOADING…</p>;
  return (
    <div data-testid="admin-overview">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { l: "Firms Tracked", v: stats.firms, tab: "firms" },
          { l: "Trader Reviews", v: stats.reviews, tab: "reviews" },
          { l: "Club Members", v: stats.traders, tab: "proofs" },
          { l: "Journal Posts", v: stats.posts, tab: "blog" },
        ].map((s) => (
          <button key={s.l} onClick={() => onNavigate(s.tab)} data-testid={`stat-${s.tab}`}
            className="card-violet px-6 py-7 text-left">
            <p className="font-mono text-3xl font-700 text-signal">{s.v}</p>
            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted">{s.l}</p>
          </button>
        ))}
      </div>
      <div className="card-violet mt-8 p-7">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-signal">{"// Desk Notes"}</p>
        <ul className="mt-4 space-y-2.5 text-sm leading-relaxed text-muted">
          <li>→ Firms tab: add new firms, edit ratings, challenges, discount codes and referral links.</li>
          <li>→ Reviews tab: moderate member reports — deletion is permanent.</li>
          <li>→ Proofs tab: verify payout certificates; verified members get badges on their reviews.</li>
          <li>→ Journal tab: publish and edit articles shown on the public blog.</li>
        </ul>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (user === false) navigate("/admin/login", { replace: true });
    else if (user && user.role !== "admin") navigate("/", { replace: true });
  }, [user, navigate]);

  if (!user || user.role !== "admin")
    return (
      <div className="flex min-h-screen items-center justify-center font-mono text-xs uppercase tracking-[0.25em] text-muted" data-testid="admin-loading">
        VERIFYING CLEARANCE<span className="animate-blink">…</span>
      </div>
    );

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="flex min-h-screen" data-testid="admin-dashboard">
      <aside className="fixed inset-y-0 left-0 z-40 flex w-16 flex-col border-r border-line bg-surface lg:w-56">
        <div className="flex h-16 items-center justify-center gap-2.5 border-b border-line lg:justify-start lg:px-5">
          <LogoMark size={30} />
          <span className="hidden font-display text-sm font-800 tracking-tight text-white lg:inline">
            FTC<span className="text-signal">ADMIN</span>
          </span>
        </div>
        <nav className="flex-1 py-4" data-testid="admin-nav">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`flex w-full items-center justify-center gap-3 border-l-2 px-5 py-3.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors lg:justify-start ${
                tab === t.key ? "border-signal bg-signal/5 text-signal" : "border-transparent text-muted hover:text-white"
              }`}
            >
              <t.icon size={15} />
              <span className="hidden lg:inline">{t.label}</span>
            </button>
          ))}
        </nav>
        <button
          onClick={() => navigate("/")}
          data-testid="admin-view-site"
          className="flex items-center justify-center gap-3 px-5 py-3.5 font-mono text-[10px] uppercase tracking-[0.2em] text-muted transition-colors hover:text-white lg:justify-start"
        >
          <ArrowLeft size={15} /> <span className="hidden lg:inline">View Site</span>
        </button>
        <button
          onClick={handleLogout}
          data-testid="admin-logout-btn"
          className="mb-4 flex items-center justify-center gap-3 px-5 py-3.5 font-mono text-[10px] uppercase tracking-[0.2em] text-loss/80 transition-colors hover:text-loss lg:justify-start"
        >
          <LogOut size={15} /> <span className="hidden lg:inline">Logout</span>
        </button>
      </aside>
      <main className="ml-16 flex-1 p-6 lg:ml-56 lg:p-10">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-signal">{"// "}{TABS.find((t) => t.key === tab).label}</p>
            <h1 className="mt-2 font-display text-3xl font-800 tracking-tight text-white capitalize">{tab}</h1>
          </div>
          <p className="hidden font-mono text-[10px] uppercase tracking-[0.2em] text-muted sm:block">{user.email}</p>
        </div>
        {tab === "overview" && <Overview onNavigate={setTab} />}
        {tab === "firms" && <FirmsTab />}
        {tab === "reviews" && <ReviewsTab />}
        {tab === "proofs" && <ProofsTab />}
        {tab === "coins" && <CoinsTab />}
        {tab === "blog" && <BlogTab />}
      </main>
    </div>
  );
}
